import { Integration_Auth_Names_Enum, Integration_Names_Enum } from '@main/graphql/types.generated';
import { z } from 'zod/v4';

import { IntegrationAuth, IntegrationConnector, IntegrationConnectorCategory } from '../../lib';

export type FreshdeskConnectorConfig = typeof FreshdeskConnector._.Config;

const freshdeskAccountHost = /^[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?\.freshdesk\.com$/;

/* The messages name their translation keys (params.i18n) for the settings form; the English
 * stays for errors raised outside it. */
// Any text that is not the account URL, a bare host included, gets the Freshdesk message, not a generic URL one.
const freshdeskBaseUrlSchema = z
  .string()
  .refine(
    (value) => {
      if (!URL.canParse(value)) return false;
      const url = new URL(value);
      return url.protocol === 'https:' && freshdeskAccountHost.test(url.hostname);
    },
    {
      message: 'Use an https://<account>.freshdesk.com URL.',
      params: { i18n: 'configureForm.validation.freshdeskUrl' },
      abort: true,
    },
  )
  .refine(
    (value) => {
      const url = new URL(value);
      // Empty query and fragment delimiters are absent from URL.search and URL.hash.
      return url.port === '' && url.href === `${url.origin}/`;
    },
    {
      message:
        'Freshdesk account URLs must not include credentials, custom ports, paths, queries, or fragments.',
      params: { i18n: 'configureForm.validation.accountUrlOnly' },
    },
  );

export const FreshdeskConnector = IntegrationConnector.make({
  provider: Integration_Names_Enum.Freshdesk,
  logoPath: 'images/freshdesk-logo.svg',
  categories: [IntegrationConnectorCategory.Support],
  auths: IntegrationAuth.group([
    IntegrationAuth.manual({
      name: Integration_Auth_Names_Enum.ApiKey,
      configSchema: z.object({
        baseUrl: freshdeskBaseUrlSchema,
      }),
      encryptedConfigSchema: z.object({
        apiKey: z.string().min(1),
      }),
    }),
  ]),
});
